import { motion } from "framer-motion";
import { Eye, Leaf, Lightbulb, Users, CheckCircle2, Target, Award, Globe } from "lucide-react";
import teamSolar from "../assets/team-solar.jpg";
import controlRoom from "../assets/control-room.jpg";
import globalNetwork from "../assets/global-network.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import StatsGrid from "../components/StatsGrid";
import EnergyFlowSVG from "../components/EnergyFlowSVG";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const timeline = [
  { year: "2019", title: "Foundation", desc: "AeroVolt Renewables was founded with a vision to democratize access to renewable energy infrastructure investment. Initial feasibility studies launched across 5 European countries." },
  { year: "2020", title: "First Wind Farm", desc: "Completed our first 45MW onshore wind farm in Scotland. Secured £120M in infrastructure funding from institutional investors and development finance institutions." },
  { year: "2021", title: "Solar Expansion", desc: "Launched solar operations in Spain and Portugal with 8 utility-scale installations. Reached 200MW combined capacity. Partner network grows to 5,000+ globally." },
  { year: "2022", title: "Grid Integration", desc: "Achieved full grid integration across all assets with signed PPAs guaranteeing fixed tariffs for 20+ years. Monthly revenue distribution begins at scale." },
  { year: "2023", title: "Global Reach", desc: "Expanded to 14 wind farms and 22 solar sites across 12 countries. Partner network surpasses 20,000 participants. Launched real-time energy monitoring dashboards." },
  { year: "2024", title: "850MW Milestone", desc: "Total installed capacity reaches 850MW. Awarded 'Best Renewable Platform' by Clean Energy World. Cumulative revenue distributed exceeds £18M to global partners." },
];

const About = () => (
  <div>
    <PageHero
      eyebrow="About AeroVolt"
      title={<>Building the Future of <span className="energy-gradient-text">Clean Energy</span> Participation</>}
      description="AeroVolt Renewables is a renewable infrastructure participation platform focused on connecting global partners with operational wind and solar energy assets generating real electricity for national power grids."
      backgroundImage={teamSolar}
    />

    {/* Stats */}
    <Section>
      <StatsGrid stats={[
        { value: "850 MW", label: "Installed Capacity" },
        { value: "36+", label: "Active Sites" },
        { value: "12", label: "Countries" },
        { value: "32,000+", label: "Global Partners" },
      ]} />
    </Section>

    {/* Mission */}
    <Section className="bg-secondary/20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fadeUp}>
          <span className="font-display text-xs tracking-[0.3em] uppercase text-primary mb-3 block">Our Mission</span>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Connecting People to the Power of Renewable Infrastructure</h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We believe that participation in clean energy generation should not be limited to large institutions. AeroVolt bridges the gap between individual investors and operational renewable energy assets, creating shared value from sustainable power generation.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Every watt of energy produced by our wind turbines and solar arrays is fed into national power grids through certified interconnection agreements and long-term Power Purchase Agreements (PPAs), generating consistent, verifiable revenue.
          </p>
          <div className="space-y-3">
            {["Fully operational, grid-connected infrastructure", "Transparent, real-time energy production monitoring", "Performance-based distributions — no guaranteed returns", "Regulated, compliant platform structure"].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="relative">
          <img src={controlRoom} alt="Energy monitoring control room" className="rounded-2xl w-full glow-box" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent rounded-2xl" />
        </motion.div>
      </div>
    </Section>

    {/* Core Values */}
    <Section>
      <SectionHeading eyebrow="Core Values" title="What Drives Us" description="Every decision at AeroVolt is guided by four foundational principles." />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: Eye, title: "Transparency", desc: "Open reporting, real-time dashboards, audited financial statements, and clear revenue tracking at every level." },
          { icon: Leaf, title: "Sustainability", desc: "Every project is designed for long-term environmental benefit, producing zero-emission electricity for 25-30+ years." },
          { icon: Lightbulb, title: "Innovation", desc: "We deploy cutting-edge turbine and photovoltaic technologies, AI-powered grid management, and predictive maintenance systems." },
          { icon: Users, title: "Community", desc: "Building a global network of energy partners united by the shared goal of accelerating the transition to clean power." },
        ].map((v, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="text-center p-8 rounded-2xl glass-card glass-card-hover transition-all duration-500 group">
            <div className="w-16 h-16 rounded-2xl energy-gradient flex items-center justify-center mx-auto mb-5 group-hover:shadow-[0_0_25px_hsl(155_80%_50%/0.3)] transition-shadow">
              <v.icon className="w-8 h-8 text-primary-foreground" />
            </div>
            <h3 className="font-display text-base font-semibold mb-3">{v.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{v.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Energy Flow */}
    <div className="overflow-hidden">
      <EnergyFlowSVG className="h-16 opacity-30" />
    </div>

    {/* Timeline */}
    <Section className="bg-secondary/20">
      <SectionHeading eyebrow="Our Journey" title="The AeroVolt Timeline" description="From concept to 850MW of installed clean energy capacity." />
      <div className="relative max-w-4xl mx-auto">
        <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/40 via-primary/20 to-primary/40" />
        {timeline.map((t, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className={`relative flex items-start mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} flex-row`}
          >
            <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-primary -translate-x-1/2 mt-1 animate-glow-pulse" />
            <div className={`ml-12 md:ml-0 md:w-1/2 ${i % 2 === 0 ? "md:pr-14 md:text-right" : "md:pl-14"}`}>
              <span className="font-display text-sm text-primary font-bold">{t.year}</span>
              <h3 className="font-display text-lg font-bold mt-1 mb-2">{t.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{t.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Global Presence */}
    <Section>
      <SectionHeading eyebrow="Global Reach" title="Powering Partners Worldwide" description="Our infrastructure and partner network spans 45+ countries across 6 continents." />
      <motion.div {...fadeUp} className="relative rounded-2xl overflow-hidden glow-box">
        <img src={globalNetwork} alt="Global partner network" className="w-full h-[400px] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Globe, label: "45+ Countries" },
              { icon: Users, label: "32,000 Partners" },
              { icon: Target, label: "36 Active Sites" },
              { icon: Award, label: "Award-Winning" },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg glass-card">
                <s.icon className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </Section>
  </div>
);

export default About;
